// A Custom board's records: whatever fields its app keeps, one entry each, so they save, sync, undo, go to the trash
// and are read by agents like a pipeline's deals. The app (a custom widget filling the board) reaches them only
// through the widget kit's messages; every change is cleaned here, in the browser before it is sent and on the
// server for all of them.

import type { Entry } from "./types";

/** What one record may hold as JSON: a few dozen fields of text and numbers, not a pasted document. */
export const MAX_RECORD_CHARS = 4_000;
/** How many records one board holds (the most entries a board keeps; the server holds the same line). */
export const MAX_RECORDS = 2_000;
/** The size a Custom board's app is kept at: it fills the board, so this only has to be one the server keeps and the app reports as. */
export const APP_SIZE = { w: 1200, h: 800 };
/** Names a record's fields cannot use: the app and the agents read these off the record itself. */
export const RESERVED_FIELDS = ["id", "created", "updated"];
/** A record in the frame, or in an agent's list: its fields flat, with its id and when it was made and last changed. */
export type RecordRow = { id: string; created: number; updated: number } & Record<string, unknown>;

export class RecordRefused extends Error {}

/** A value with each string that is a record's id (a key too) swapped for its new one: an app's saved state, on a copy of its records. */
export function swapIds(v: unknown, to: Map<string, string>): unknown {
  if (typeof v === "string") return to.get(v) ?? v;
  if (Array.isArray(v)) return v.map((x) => swapIds(x, to));
  if (v && typeof v === "object") return Object.fromEntries(Object.entries(v).map(([k, x]) => [to.get(k) ?? k, swapIds(x, to)]));
  return v;
}

const DEPTH = 6;

/** Plain JSON no deeper than a sensible record goes, or why not. */
function plain(v: unknown, depth: number): unknown {
  if (v === null || typeof v === "string" || typeof v === "boolean") return v;
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (depth > DEPTH) throw new RecordRefused(`A record's fields go up to ${DEPTH} levels deep.`);
  if (Array.isArray(v)) return v.map((x) => plain(x === undefined ? null : x, depth + 1));
  if (typeof v === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, x] of Object.entries(v as Record<string, unknown>)) if (x !== undefined && typeof x !== "function") out[k.slice(0, 80)] = plain(x, depth + 1);
    return out;
  }
  throw new RecordRefused("A record holds plain JSON: text, numbers, true or false, lists and objects.");
}

/**
 * A record's fields as they are kept: plain JSON, its reserved names (id, created, updated) taken off, a null field
 * taken off too, within MAX_RECORD_CHARS. A record sent back whole, with its id and times, keeps only its own fields.
 */
export function cleanRecordFields(raw: unknown): Record<string, unknown> {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) throw new RecordRefused("A record is an object of fields, like {title: \"Draft post\", stage: \"Idea\"}.");
  const out = plain(raw, 0) as Record<string, unknown>;
  for (const k of RESERVED_FIELDS) delete out[k];
  // a field that is null is not there: update takes one off that way, and an Undo of a field's first value sends it
  for (const k of Object.keys(out)) if (out[k] === null) delete out[k];
  const size = JSON.stringify(out).length;
  if (size > MAX_RECORD_CHARS) throw new RecordRefused(`That record is ${size.toLocaleString("en-US")} characters, and a record holds up to ${MAX_RECORD_CHARS.toLocaleString("en-US")}. Keep long text out of it.`);
  return out;
}

/** A record's fields with a patch laid over them: the fields the patch sets change, the rest stay, and one set to null is taken off. */
export function mergeFields(was: Record<string, unknown>, patch: unknown): Record<string, unknown> {
  const set = cleanRecordFields(patch);
  const next: Record<string, unknown> = { ...was, ...set };
  for (const [k, v] of Object.entries(patch as Record<string, unknown>)) if (v === null) delete next[k];
  return cleanRecordFields(next);
}

/** The records of a board's entries, oldest first (the order they were made in). */
export function recordsOf(entries: Entry[]): Entry[] {
  return entries.filter((e) => e.t === "record").sort((a, b) => a.order - b.order || a.createdAt - b.createdAt || (a.id < b.id ? -1 : 1));
}

/** A record as the app and the agents see it: its fields flat, then the three names that are the board's. */
export function recordRow(e: Pick<Entry, "id" | "data" | "createdAt" | "updatedAt">): RecordRow {
  return { ...e.data, id: e.id, created: e.createdAt, updated: e.updatedAt };
}

/** One record in a line: its id first, its fields as JSON, cut at `max` characters. */
export function recordLine(e: Pick<Entry, "id" | "data">, max = 400): string {
  const j = JSON.stringify(e.data);
  return `- ${e.id} ${j.length > max ? `${j.slice(0, max)}… (cut: read_records gives it whole)` : j}`;
}

/** A record by its first words: its first text field, or "A record". */
export function recordName(e: Pick<Entry, "data">, max = 60): string {
  const t = Object.values(e.data).find((v) => typeof v === "string" && v.trim());
  return typeof t === "string" ? (t.length > max ? `${t.slice(0, max)}…` : t) : "A record";
}

/** How many records, in words. */
export const recordCount = (n: number) => (n === 1 ? "1 record" : `${n} records`);
